/**
 * Photo prompt library — the canonical list of "standard evidence" shots the
 * inspector should capture for each job, BEFORE the per-clause photo flow
 * runs. Surfaces these as a prompted list in the field PWA so the inspector
 * doesn't have to remember (and the tech head doesn't have to manually curate
 * after the fact).
 *
 * Sources:
 *   - "Essential Battery Inspection Photos" (`Battery Inspection Photo List.docx`,
 *     Leading Edge, May 2026). 26 prompts: inverter shots, isolator photos
 *     (before / during / after), battery system labelling + earthing + mounting,
 *     and the safety-signage photos required by AS/NZS 5139.
 *   - Cross-referenced against the older `Battery system minimum photos.docx`
 *     categories captured in `docs/PHOTO_NAMING.md` — that doc adds a
 *     "Switchboard" category (door-closed, escutcheon-on/off, etc.) which
 *     the May 2026 photo-list docx does NOT include. When the team decides
 *     whether those switchboard shots belong in the standard list, add them
 *     here with `subject: 'Switchboard'`.
 *
 * appliesTo semantics:
 *   - `all`              — every battery job needs this shot
 *   - `integrated-bs`    — Section 4 (Integrated BESS) only — currently none,
 *                          slot reserved so the schema supports future entries
 *                          parsed from a Section 4 photo doc
 *   - `pre-assembled-bs` — Section 5 (Pre-assembled BS, e.g. Tesla Powerwall)
 *                          only — the inverter-specific shots live here
 *   - `pv`               — solar-PV-only inspections — currently none in this
 *                          docx (Leading Edge's PV-side photo list comes from
 *                          T27 `T27 photo naming.docx`; capture those subjects
 *                          via `STANDARD_PHOTO_SUBJECTS` in `photo-naming.ts`
 *                          for now)
 *
 * Don't hand-edit the JSON data file — regenerate it via the docx parser
 * (the steps are spelled out in the commit that introduced this file) so the
 * source remains the Word doc the technical head maintains.
 */
import { z } from 'zod';
import photoPromptsData from './photo-prompts-data.json' with { type: 'json' };
/**
 * Which job kinds a given prompt applies to. Used by `getPromptsForJob` to
 * filter the library down to the prompts the inspector should see for a
 * specific job.
 *
 *   - `all`              — applies to every battery job regardless of system type
 *   - `integrated-bs`    — Section 4 only
 *   - `pre-assembled-bs` — Section 5 only
 *   - `pv`               — PV-only inspections
 */
export const PhotoPromptAppliesToSchema = z.enum([
    'all',
    'integrated-bs',
    'pre-assembled-bs',
    'pv',
]);
/**
 * One prompt in the library. `promptKey` is the stable identifier that the
 * PWA stores on `FieldPhoto.promptKey` when the inspector captures the shot,
 * and that the backend uses to write the photo to a predictable
 * `Photos for Report/` filename downstream — no manual rename required.
 */
export const PhotoPromptSchema = z.object({
    /**
     * Stable key of the form `<subject-slug>-<shot-slug>`. Must be unique
     * across the library; sanitised by `makePhotoRecord` when used in
     * filenames so non-alphanumerics get collapsed to `-`.
     */
    promptKey: z.string().min(1),
    /**
     * Visual grouping in the PWA UI — e.g. "Inverter", "Battery system",
     * "Isolators", "PV array". Multiple prompts share a subject.
     */
    subject: z.string().min(1),
    /**
     * Short description of the shot the inspector takes — exact text from
     * the source docx. e.g. "Inverter label and serial number".
     */
    shot: z.string().min(1),
    /**
     * Optional longer-form description of what this photo evidences. Surfaced
     * as helper text under the shot label in the PWA.
     */
    description: z.string().optional(),
    /** Which job kinds this prompt applies to. */
    appliesTo: PhotoPromptAppliesToSchema,
    /**
     * When false, the prompt is genuinely optional — typically because the
     * shot is conditional on a feature being present (e.g. "arc flash hazard
     * signage (if applicable)"). The PWA's progress counter excludes
     * non-required prompts from the denominator unless they've been captured.
     */
    required: z.boolean(),
});
const rawPrompts = photoPromptsData;
/**
 * Prompts that are NOT in the source docx but that the report demands anyway.
 *
 * Kept here rather than in the generated JSON so the docx parser can be re-run
 * without losing them. Each one needs a line in the module docblock above
 * saying why the docx omits it.
 *
 *   - `switchboard` — MANDATORY_HARDWARE_PHOTOS requires a switchboard shot on
 *     every battery job, and the May 2026 battery photo-list docx has no
 *     Switchboard subject (docs/PHOTO_NAMING.md does, on the PV side). Without
 *     this prompt the PWA offers no way to capture it and the dashboard's
 *     `hardware-photos` QA rule can never pass.
 */
const ADDENDUM_PROMPTS = [
    {
        promptKey: 'switchboard',
        subject: 'Switchboard',
        shot: 'Switchboard',
        description: 'Switchboard with the escutcheon on — legible, in context.',
        appliesTo: 'all',
        required: true,
    },
];
/* ────────────────────────────────────────────────────────────────────────────
 * Public surface
 * ──────────────────────────────────────────────────────────────────────────── */
/** Every prompt in the library, in document order (addenda last). */
export const PhotoPromptLibrary = [
    ...rawPrompts,
    ...ADDENDUM_PROMPTS,
].map((p) => ({
    promptKey: p.promptKey,
    subject: p.subject,
    shot: p.shot,
    ...(p.description !== undefined ? { description: p.description } : {}),
    appliesTo: p.appliesTo,
    required: p.required,
}));
/** Build a promptKey-keyed lookup map. Memoised at module init. */
const lookupMap = new Map();
for (const entry of PhotoPromptLibrary) {
    lookupMap.set(entry.promptKey, entry);
}
/**
 * Resolve a promptKey to its library entry. Returns undefined if the key
 * doesn't match any prompt — the PWA falls back to rendering the raw key
 * in that case (so legacy or clause-bound photos still surface).
 */
export function findPromptByKey(key) {
    return lookupMap.get(key.trim());
}
/**
 * Filter the library down to the prompts the inspector should see for a
 * given job.
 *
 *   - `inspectionType='pv'`            — only prompts with `appliesTo='pv'`
 *                                        or `appliesTo='all'`
 *   - `inspectionType='battery'`       — all battery-relevant prompts, further
 *                                        filtered by `batterySystemType`:
 *       - `'pre-assembled'`            — include `pre-assembled-bs` + `all`,
 *                                        exclude `integrated-bs`
 *       - `'integrated'`               — include `integrated-bs` + `all`,
 *                                        exclude `pre-assembled-bs`
 *       - `undefined`                  — include every battery-relevant
 *                                        prompt (used when the inspector
 *                                        hasn't picked a system type yet)
 *   - `inspectionType='combo'`         — return everything; combos cover
 *                                        both a PV and a battery system
 *
 * Always returns a readonly array — callers should not mutate.
 */
export function getPromptsForJob(inspectionType, batterySystemType) {
    if (inspectionType === 'combo') {
        return PhotoPromptLibrary;
    }
    if (inspectionType === 'pv') {
        return PhotoPromptLibrary.filter((p) => p.appliesTo === 'pv');
    }
    // 'battery'
    return PhotoPromptLibrary.filter((p) => {
        // PV-only prompts never apply to a pure battery job.
        if (p.appliesTo === 'pv')
            return false;
        if (p.appliesTo === 'all')
            return true;
        if (batterySystemType === 'pre-assembled') {
            return p.appliesTo === 'pre-assembled-bs';
        }
        if (batterySystemType === 'integrated') {
            return p.appliesTo === 'integrated-bs';
        }
        // System type not yet picked — include both branches.
        return p.appliesTo === 'pre-assembled-bs' || p.appliesTo === 'integrated-bs';
    });
}
//# sourceMappingURL=photo-prompts.js.map